import { Alert, AlertDescription, AlertTitle } from "@repo/ui/components/alert"
import { Button } from "@repo/ui/components/button"
import { TriangleAlert } from "lucide-react"
import { Component, type ErrorInfo, type ReactNode } from "react"

interface Props {
  children: ReactNode
  /** Changing this (the address, for instance) clears a shown error. */
  resetKey: string
}

interface State {
  error: Error | null
}

/**
 * Catches a page that fails while drawing, so the sidebar and top bar stay and
 * the message is on screen instead of a blank page and a console warning.
 */
export class RouteErrorBoundary extends Component<Props, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("A page failed to draw:", error, info.componentStack)
  }

  componentDidUpdate(previous: Props) {
    if (this.state.error && previous.resetKey !== this.props.resetKey) {
      this.setState({ error: null })
    }
  }

  render() {
    const { error } = this.state

    if (!error) return this.props.children

    // After the dev server restarts or the app is updated, an open tab asks for
    // files that no longer exist under that name. A reload is the fix.
    const stale =
      /dynamically imported module|Importing a module script failed|Loading chunk/i.test(
        error.message
      )

    return (
      <div className="flex max-w-xl flex-col gap-4">
        <Alert variant="destructive">
          <TriangleAlert />
          <AlertTitle>
            {stale
              ? "This page is out of date"
              : "This page could not be shown"}
          </AlertTitle>
          <AlertDescription>
            {stale
              ? "The app was updated since you opened it. Reload to get the new version."
              : error.message || "Something went wrong while drawing the page."}
          </AlertDescription>
        </Alert>
        <div className="flex gap-2">
          <Button onClick={() => window.location.reload()}>Reload</Button>
          <Button
            variant="outline"
            onClick={() => this.setState({ error: null })}
          >
            Try again
          </Button>
        </div>
      </div>
    )
  }
}
